import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@mono/ui-components';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useUpdateProject } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
export function EditProjectDialog({ open, onOpenChange, project }) {
    const [name, setName] = useState(project.name);
    const [description, setDescription] = useState(project.description ?? '');
    const [touched, setTouched] = useState(false);
    const updateProject = useUpdateProject();
    // Validation
    const trimmedName = name.trim();
    const isNameEmpty = trimmedName.length === 0;
    const showNameError = touched && isNameEmpty;
    const isValid = !isNameEmpty;
    // Reset form when dialog opens with new project
    useEffect(()=>{
        if (open) {
            setName(project.name);
            setDescription(project.description ?? '');
            setTouched(false);
        }
    }, [
        open,
        project
    ]);
    const handleSubmit = async (e)=>{
        e.preventDefault();
        setTouched(true);
        if (!isValid) {
            return;
        }
        // Check if anything changed
        const nameChanged = name.trim() !== project.name;
        const descriptionChanged = description.trim() !== (project.description ?? '');
        if (!nameChanged && !descriptionChanged) {
            toast.info('No changes to save');
            onOpenChange(false);
            return;
        }
        try {
            await updateProject.mutateAsync({
                projectId: project.projectId,
                name: nameChanged ? name.trim() : undefined,
                description: descriptionChanged ? description.trim() || undefined : undefined
            });
            toast.success('Project updated successfully');
            onOpenChange(false);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to update project'));
        }
    };
    const handleCancel = ()=>{
        setName(project.name);
        setDescription(project.description ?? '');
        setTouched(false);
        onOpenChange(false);
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "sm:max-w-[500px]",
            "data-testid": "edit-project-dialog",
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Edit Project"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Update your project's name and description"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit,
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    htmlFor: "edit-name",
                                    className: "text-sm font-medium",
                                    children: [
                                        "Project Name ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-red-500",
                                            children: "*"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("input", {
                                    id: "edit-name",
                                    type: "text",
                                    value: name,
                                    onChange: (e)=>setName(e.target.value),
                                    onBlur: ()=>setTouched(true),
                                    placeholder: "e.g., NASA Cloud Services RFP",
                                    required: true,
                                    "aria-required": "true",
                                    "aria-invalid": showNameError,
                                    "aria-describedby": showNameError ? 'edit-name-error edit-name-hint' : 'edit-name-hint',
                                    maxLength: 200,
                                    className: `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${showNameError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'}`,
                                    disabled: updateProject.isPending,
                                    "data-testid": "edit-project-name-input"
                                }),
                                showNameError && /*#__PURE__*/ _jsx("p", {
                                    id: "edit-name-error",
                                    className: "text-xs text-red-500",
                                    role: "alert",
                                    children: "Project name is required"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    id: "edit-name-hint",
                                    className: "text-xs text-gray-500",
                                    children: [
                                        name.length,
                                        "/200 characters"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    htmlFor: "edit-description",
                                    className: "text-sm font-medium",
                                    children: [
                                        "Description ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-gray-400",
                                            children: "(optional)"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("textarea", {
                                    id: "edit-description",
                                    value: description,
                                    onChange: (e)=>setDescription(e.target.value),
                                    placeholder: "Briefly describe the purpose of this project",
                                    rows: 4,
                                    "aria-describedby": "edit-description-hint",
                                    maxLength: 2000,
                                    className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none",
                                    disabled: updateProject.isPending,
                                    "data-testid": "edit-project-description-input"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    id: "edit-description-hint",
                                    className: "text-xs text-gray-500",
                                    children: [
                                        description.length,
                                        "/2000 characters"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(DialogFooter, {
                            className: "gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    variant: "outline",
                                    onClick: handleCancel,
                                    disabled: updateProject.isPending,
                                    "aria-label": "Cancel editing project",
                                    "data-testid": "edit-project-cancel-button",
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "submit",
                                    isLoading: updateProject.isPending,
                                    disabled: updateProject.isPending || touched && !isValid,
                                    "aria-label": "Save project changes",
                                    "data-testid": "edit-project-submit-button",
                                    children: "Save Changes"
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
